/**
 * Web push notifications (FCM). iOS supports web push for installed
 * home-screen apps (16.4+); permission must be requested from a user gesture.
 * Tokens land in users/{uid}.fcmTokens — the same array the backend's
 * sendPush() fans out to, so web devices get the exact same notifications
 * as the native apps.
 */
import { getMessaging, getToken, isSupported } from "firebase/messaging";
import { arrayUnion, doc, setDoc } from "firebase/firestore";
import { app, db } from "../firebase";

const VAPID_KEY = import.meta.env.VITE_FB_VAPID_KEY as string | undefined;

export type PushStatus =
  | "enabled"          // token registered
  | "denied"           // user (or OS) refused notification permission
  | "unsupported"      // browser/context can't do web push (e.g. Safari tab)
  | "unconfigured"     // no VAPID key baked into this build
  | "error";

/** Current permission state, for rendering the Profile row. */
export function pushPermission(): NotificationPermission | "unsupported" {
  return "Notification" in window ? Notification.permission : "unsupported";
}

/**
 * Ask for permission and register this device's push token.
 * MUST be called from a click handler (iOS requirement).
 */
export async function enablePush(uid: string): Promise<PushStatus> {
  try {
    if (!VAPID_KEY) return "unconfigured";
    if (!("Notification" in window) || !("serviceWorker" in navigator)) return "unsupported";
    if (!(await isSupported().catch(() => false))) return "unsupported";

    const permission = await Notification.requestPermission();
    if (permission !== "granted") return "denied";

    const registration = await navigator.serviceWorker.ready;
    const token = await getToken(getMessaging(app), {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return "error";

    await setDoc(doc(db, "users", uid), { fcmTokens: arrayUnion(token) }, { merge: true });
    return "enabled";
  } catch {
    return "error";
  }
}
